import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Button as MButton,
  Modal,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import Input from "../Common/Input";
import Button from "../Common/Button";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import {
  addCourse,
  addOrgCourse,
  searchAdminCourses,
  searchCourses,
  updateCourse,
  updateOrgCourse,
} from "../../store/course/course.action";
import { covertURl, uploadFile } from "../../util/AwsFunction";
import noImageFrom from "../../assets/images/noimage.png";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";

import languages from "../../constants/languages.json";
import { closeDialogue, handleOpenNew } from "../../util/Dialogue";
import Select from "../Common/Select";
import CertificatePreview from "../Common/CertificatePreview";
import Textarea from "../Common/Textarea";
import { checkSubscriptionApi } from "../../store/organization/organization.action";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "13px",
  border: "1px solid #C9C9C9",
  boxShadow: 24,
  p: "19px",
  maxHeight: "calc(100% - 32px)",
  overflow: "auto",
};

const CreateCourse = ({
  isEditing = false,
  editingCourseData = null,
  onEditSuccess,
}) => {
  const dispatch = useDispatch();
  const { tokenData } = useSelector((state) => state.loggedUser);
  const { departments } = useSelector((state) => state.organizationUser);
  const { dialogue } = useSelector((state) => state.dialogue);

  const [activeStep, setActiveStep] = useState(0);

  // Step 1
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [language, setLanguage] = useState("");
  const [difficulty, setDifficulty] = useState(1);
  const [thumbnail, setThumbnail] = useState("");
  const [thumbnailImage, setThumbnailImage] = useState("");
  const [pointsToLearn, setPointsToLearn] = useState([]);
  const [tags, setTags] = useState("");
  const [suitableForDepartments, setSuitableForDepartments] = useState([]);
  const [prerequisiteCourse, setPrerequisiteCourse] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  // Step 2
  const [privacy, setPrivacy] = useState("public");
  const [priceType, setPriceType] = useState("free");
  const [amount, setAmount] = useState(0);
  const [enableCertification, setEnableCertification] = useState(false);
  // Certificate Details
  const [certificateSetting, setCertificateSetting] = useState({
    title: "CERTIFICATE OF COMPLETION",
    line1: "This certificate is presented to",
    line2: "in recognition of successfully completing the course",
    receivedIn: "",
    bgImage: "",
    numberOfLogos: 1,
    numberOfIssuer: 1,
  });
  const [logos, setLogos] = useState([]);
  const [authorizers, setAuthorizers] = useState([]);
  const [bgFile, setBgFile] = useState(null);
  const [bgBase64, setBgBase64] = useState("");

  const [errors, setError] = useState({
    title: "",
    amount: "",
  });

  const closePopup = () => {
    closeDialogue(dispatch);
    onEditSuccess();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (isEditing && editingCourseData) {
      setPriceType(editingCourseData.priceType);
    } else if (!enableCertification) {
      setPriceType("free");
    }
  }, [enableCertification]);

  useEffect(() => {
    if (priceType === "free") {
      setAmount(0);
      setError({
        ...errors,
        amount: "",
      });
    }
  }, [priceType]);

  useEffect(() => {
    if (isEditing && editingCourseData) {
      setTitle(editingCourseData.title);
      setDescription(editingCourseData.description);
      setLanguage(editingCourseData.language);
      setDifficulty(editingCourseData.difficulty || 1);
      setThumbnail(editingCourseData.thumbnail || "");
      setThumbnailImage(editingCourseData.thumbnail);
      setPrerequisiteCourse(
        (editingCourseData.prerequisiteCourse && {
          ...editingCourseData.prerequisiteCourse,
          title: `${editingCourseData.prerequisiteCourse.uniqueId} - ${editingCourseData.prerequisiteCourse.title}`,
        }) ||
          null,
      );
      setPointsToLearn(editingCourseData.pointsToLearn || []);
      setTags(editingCourseData.tags?.join(", "));
      setPrivacy(
        editingCourseData.privacy ||
          (tokenData?.isOrg ? "org_private" : "public"),
      );
      setPriceType(editingCourseData.priceType);
      setAmount(editingCourseData.amount);
      setEnableCertification(editingCourseData.enableCertification);

      setCertificateSetting(
        editingCourseData.certificateSettings?.contents || {
          title: "CERTIFICATE OF COMPLETION",
          line1: "This certificate is present to",
          line2: "in recognition of successfully completing the course in",
          receivedIn: "",
          bgImage: "",
          numberOfLogos: 1,
          numberOfIssuer: 1,
        },
      );
      setBgBase64(
        editingCourseData.certificateSettings?.contents?.bgImage || "",
      );

      setLogos(
        editingCourseData.certificateSettings?.contents?.logos?.map((url) => ({
          url,
        })) || [],
      );

      setAuthorizers(
        editingCourseData.certificateSettings?.contents?.authorizers?.map(
          (item) => ({
            signatureImage: item.signatureImage,
            name: item.authorizerName,
            designation: item.authorizerDesignation,
          }),
        ) || [],
      );
    } else {
      setPrivacy(tokenData?.isOrg ? "org_private" : "private");
    }
  }, [isEditing, editingCourseData, tokenData]);

  // Handle thumbnail image upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = "";

    setError({
      ...errors,
      thumbnail: "",
    });
  };

  const handleCertificateBGUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBgFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBgBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }

    e.target.value = "";
  };

  // Ponits to Learn handler - START
  //
  const handleAddPointField = () => {
    setPointsToLearn((prev) => [...prev, ""]);
  };

  const handlePointChange = (index, value) => {
    setPointsToLearn((prev) => {
      let tempVar = [...prev];
      tempVar[index] = value;
      return tempVar;
    });
  };

  const handleRemovePoint = (index) => {
    setPointsToLearn((prev) => prev.filter((_, idx) => index != idx));
  };
  //
  // Ponits to Learn handler - END

  // Certicate data change handlers - START
  //
  const handleCertficateChange = (e) => {
    const { name, value } = e.target;
    setCertificateSetting((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Handle certificate bg upload

  const handleLogoChanges = ({ logo, logoUrl }, index) => {
    const tempVar = [...logos];
    tempVar[index] = { file: logo, url: logoUrl };
    setLogos(tempVar);
  };

  const handleAuthorizerSignChanges = ({ file, imageUrl }, index) => {
    const tempVar = [...authorizers];
    tempVar[index] = {
      ...tempVar[index],
      file: file,
      signatureImage: imageUrl,
    };
    setAuthorizers(tempVar);
  };

  const handleAuthorizerChanges = (authorizer, index) => {
    const tempVar = [...authorizers];
    tempVar[index] = authorizer;
    setAuthorizers(tempVar);
  };
  //
  // Certicate data change handlers - END

  const handleSubmit = async () => {
    let temp_thumbnail = thumbnail;

    if (
      !title ||
      (priceType === "paid" && (!amount || amount <= 0)) ||
      (!isEditing && !(temp_thumbnail || thumbnailFile)) ||
      !language
    ) {
      let error = {};
      if (!title) error.title = "Title is required!";
      if (priceType === "paid" && (!amount || amount <= 0))
        error.amount = "Price is required!";
      if (!isEditing && !temp_thumbnail)
        error.thumbnail = "Thumbnail image is required!";
      if (!language) error.language = "Language is required!";

      if (error.title || error.thumbnail || error.language) {
        setActiveStep(0);
      }

      return setError({ ...error });
    }

    if (!isEditing && tokenData.isOrg) {
      const checkSubscription = await checkSubscriptionApi();
      if (!checkSubscription) {
        return;
      }
    }

    let logosSetting, authData;

    if (enableCertification) {
      const error = {};
      if (
        logos.length < certificateSetting.numberOfLogos ||
        !logos.every((logo) => logo.url || logo.file)
      ) {
        error.logos = "Logo is required!";
      }

      if (
        authorizers.length < certificateSetting.numberOfIssuer ||
        !authorizers.every(
          (authorizer) => authorizer.signatureImage || authorizer.file,
        )
      ) {
        error.authorizers = "Authorizer's data is required!";
      } else if (
        authorizers.length < certificateSetting.numberOfIssuer ||
        !authorizers.every((authorizer) => authorizer.name)
      ) {
        error.authorizers = "Authorizer's data is required!";
      }

      if (error.logos || error.authorizers) {
        return setError(error);
      } else {
        setError({});
      }

      logosSetting = await Promise.all(
        logos.slice(0, certificateSetting.numberOfLogos).map(async (logo) => {
          let logoUrl = logo.url;
          if (logo.file) {
            const { resDataUrl } = await uploadFile(
              logo.file,
              tokenData.isOrg
                ? `courseImage/certificatelogos/org_${tokenData.organizationId}`
                : `courseImage/certificatelogos/admin_${tokenData._id}`,
            );
            logoUrl = resDataUrl;
          }
          return logoUrl;
        }),
      );

      authData = await Promise.all(
        authorizers
          .slice(0, certificateSetting.numberOfIssuer)
          .map(async (authorizer) => {
            const auth = {
              signatureImage: authorizer.signatureImage,
              authorizerName: authorizer.name?.trim(),
              authorizerDesignation: authorizer.designation?.trim(),
            };

            if (authorizer.file) {
              const { resDataUrl } = await uploadFile(
                authorizer.file,
                tokenData.isOrg
                  ? `courseImage/certificateSignature/org_${
                      tokenData.organizationId
                    }_${authorizer?.name?.trim()?.replace(" ", "_")}`
                  : `courseImage/certificateSignature/admin_${
                      tokenData._id
                    }_${authorizer?.name?.trim()?.replace(" ", "_")}`,
              );
              auth.signatureImage = resDataUrl;
            }

            return auth;
          }),
      );
    }

    const temp_cert = { ...certificateSetting };
    if (bgFile) {
      const { resDataUrl } = await uploadFile(
        bgFile,
        "courseImage/certificateBG",
      );
      temp_cert.bgImage = resDataUrl;
    }

    if (thumbnailFile) {
      const { resDataUrl } = await uploadFile(thumbnailFile, "courseImage");
      temp_thumbnail = resDataUrl;
    }

    const payload = {
      title,
      description,
      priceType,
      amount: Number(amount),
      language,
      difficulty: Number(difficulty),
      thumbnail: temp_thumbnail,
      tags: tags?.split(",")?.map((tag) => tag.trim()),
      suitableForDepartments,
      pointsToLearn: pointsToLearn.filter((point) => point),
      privacy,
      enableCertification,
      prerequisiteCourseId: prerequisiteCourse?._id || null,
      certificateSettings: enableCertification
        ? {
            contents: {
              ...temp_cert,
              logos: logosSetting,
              authorizers: authData,
            },
          }
        : null,
    };

    console.log("CREATE COURSE ", payload);

    if (isEditing) {
      if (tokenData.isOrg) {
        dispatch(updateOrgCourse(editingCourseData._id, payload));
      } else {
        dispatch(updateCourse(editingCourseData._id, payload));
      }
      closePopup();
    } else {
      if (tokenData.isOrg) {
        // Org's Route
        dispatch(addOrgCourse(payload));
      } else {
        // Admin's Route
        dispatch(addCourse(payload));
      }
      handleOpenNew(dispatch, "createLesson");
    }
  };

  return (
    <Modal
      open={dialogue}
      onClose={closePopup}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} className="create-course-modal">
        <Typography
          id="modal-modal-title"
          className="modal-title"
          variant="h6"
          component="h2"
          sx={{ paddingBottom: "12px" }}
        >
          Add Course
        </Typography>
        <form>
          <Stepper alternativeLabel activeStep={activeStep}>
            <Step>
              <StepLabel>Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Certification</StepLabel>
            </Step>
          </Stepper>
          {activeStep === 0 && (
            <div className="row align-items-start mt-4">
              <div className="col-12 col-md-6 row gap-3">
                <div className="col-12">
                  <Input
                    label="Title"
                    name="title"
                    placeholder="Enter Title.."
                    value={title}
                    onValueChange={(value) => {
                      setTitle(value);

                      if (!value) {
                        setError({
                          ...errors,
                          title: "Title is required!",
                        });
                      } else {
                        setError({
                          ...errors,
                          title: "",
                        });
                      }
                    }}
                    errorMessage={errors.title && errors.title}
                  />
                </div>
                <div className="col-12">
                  <Input
                    type="file"
                    label="Thumbnail Image"
                    accept={"image/png, image/jpeg"}
                    onChange={handleFileUpload}
                    errorMessage={errors.thumbnail && errors.thumbnail}
                  />
                </div>
                {thumbnailImage && (
                  <div className="col-12 mt-2 fake-create-img mb-2">
                    <img
                      src={thumbnailImage}
                      onError={(e) => {
                        e.target.src = noImageFrom;
                      }}
                      alt="Course thumbnail"
                    />
                  </div>
                )}
                <div className="col-12">
                  <Textarea
                    label="Description"
                    placeholder={"Enter Details..."}
                    rows={4}
                    maxLength={500}
                    value={description}
                    onValueChange={(value) => {
                      setDescription(value);
                    }}
                  />
                </div>

                <div className="col-12">
                  <Textarea
                    label="Tags"
                    placeholder="Enter comma separated tags..."
                    rows={3}
                    maxLength={500}
                    value={tags}
                    onValueChange={(value) => {
                      setTags(value);
                    }}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6 row gap-3">
                {tokenData?.isOrg && (
                  <div className="col-12">
                    <Select
                      label={"Suitable for"}
                      placeholder={"Select Departments"}
                      multiple
                      options={departments.map((dep) => {
                        return { label: dep.name, value: dep._id };
                      })}
                      value={suitableForDepartments}
                      onValueChange={(value) => {
                        setSuitableForDepartments(value);
                      }}
                    />
                  </div>
                )}

                <div className="col-12">
                  <div className="custom-input">
                    <label>Prerequisite Courses</label>
                    <CourseDropdown
                      selected={prerequisiteCourse}
                      onSelect={setPrerequisiteCourse}
                      currentCourseId={editingCourseData?._id || ""}
                    />
                  </div>
                </div>

                <div className="col-12 row mx-0 px-0 row-gap-3">
                  <div className="col-12 col-md-6">
                    <Select
                      label={"Language"}
                      placeholder="Select Language"
                      options={languages.map((lang) => ({
                        label: lang.name,
                        value: lang.name,
                      }))}
                      value={language}
                      onValueChange={(value) => {
                        setLanguage(value);
                        if (!value) {
                          return setError({
                            ...errors,
                            language: `Language Is Required !`,
                          });
                        } else {
                          return setError({
                            ...errors,
                            language: "",
                          });
                        }
                      }}
                      errorMessage={errors.language}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <Select
                      label={"Difficulty Level"}
                      placeholder={"Select Difficulty Level"}
                      options={[
                        { label: "1", value: 1 },
                        { label: "2", value: 2 },
                        { label: "3", value: 3 },
                        { label: "4", value: 4 },
                        { label: "5", value: 5 },
                      ]}
                      value={difficulty}
                      onValueChange={(value) => {
                        setDifficulty(Number(value));
                      }}
                    />
                  </div>
                </div>

                <div className="col-12 pt-2 mb-5 custom-input">
                  <div className="d-flex justify-content-between">
                    <label>Points to Learn</label>
                    <MButton variant="text" onClick={handleAddPointField}>
                      Add
                    </MButton>
                  </div>
                  <ul>
                    {pointsToLearn.map((point, index) => {
                      return (
                        <li
                          key={index}
                          className="mb-2 d-flex align-items-center"
                        >
                          <Input
                            placeholder={`Point ${index + 1}`}
                            inputContainerClass="flex-grow-1 mb-0"
                            value={point}
                            onValueChange={(value) =>
                              handlePointChange(index, value)
                            }
                          />
                          <IconButton onClick={() => handleRemovePoint(index)}>
                            <CloseIcon />
                          </IconButton>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeStep === 1 && (
            <div className="row mt-4" style={{ minHeight: "20rem" }}>
              <div className="col-12 col-md-6">
                <div className="col-6 custom-input">
                  <label>Privacy</label>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={privacy}
                    onChange={(e) => setPrivacy(e.target.value)}
                  >
                    {tokenData?.isOrg ? (
                      <FormControlLabel
                        value="org_private"
                        control={<Radio />}
                        label="Organization"
                      />
                    ) : (
                      <FormControlLabel
                        value="private"
                        control={<Radio />}
                        label="Private"
                      />
                    )}
                    <FormControlLabel
                      value="public"
                      control={<Radio />}
                      label="Public"
                    />
                  </RadioGroup>
                </div>

                {enableCertification ? (
                  <div className="col-12">
                    <div className="row">
                      <div className="col-6">
                        <Select
                          label={"Price Type"}
                          placeholder={"Select Price Type"}
                          options={[
                            { label: "Free", value: "free" },
                            { label: "Paid", value: "paid" },
                          ]}
                          value={priceType}
                          onValueChange={(value) => {
                            setPriceType(value);
                          }}
                        />
                      </div>
                      {priceType === "paid" && (
                        <div className="col-6">
                          <Input
                            label={"Amount"}
                            name={"amount"}
                            type={"number"}
                            placeholder={"Enter Price"}
                            value={amount}
                            errorMessage={errors.amount && errors.amount}
                            onValueChange={(value) => {
                              setAmount(value);
                              if (
                                priceType === "paid" &&
                                (!value || value < 1)
                              ) {
                                return setError({
                                  ...errors,
                                  amount: `Price is required !`,
                                });
                              } else {
                                return setError({
                                  ...errors,
                                  amount: "",
                                });
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="col-12 col-md-6">
                <div className="col-12 mt-3">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableCertification}
                        onChange={(e) =>
                          setEnableCertification(e.target.checked)
                        }
                      />
                    }
                    label="Enable Certification"
                  />
                </div>
              </div>

              {enableCertification ? (
                <div className="col-12 row mt-3">
                  <Typography
                    variant="h5"
                    component={"h6"}
                    className="text-center mb-2"
                  >
                    Certificate Content
                  </Typography>
                  <div className="col-12 col-md-6">
                    <Input
                      label="Certificate of"
                      name="title"
                      placeholder="Certificate of"
                      value={certificateSetting.title}
                      onChange={handleCertficateChange}
                      errorMessage={errors.title}
                    />

                    <div className="row mt-3">
                      <div className="col-6">
                        <div className="custom-input">
                          <label>Number of logos</label>
                          <RadioGroup
                            row
                            aria-labelledby="logos-radio-buttons-group-label"
                            name="numberOfLogos"
                            value={certificateSetting.numberOfLogos}
                            onChange={handleCertficateChange}
                          >
                            <FormControlLabel
                              value={0}
                              control={<Radio />}
                              label="0"
                            />
                            <FormControlLabel
                              value={1}
                              control={<Radio />}
                              label="1"
                            />
                            <FormControlLabel
                              value={2}
                              control={<Radio />}
                              label="2"
                            />
                          </RadioGroup>
                        </div>

                        <div className="custom-input">
                          <label>Number of Signatories</label>
                          <RadioGroup
                            row
                            aria-labelledby="issuer-radio-buttons-group-label"
                            name="numberOfIssuer"
                            value={certificateSetting.numberOfIssuer}
                            onChange={handleCertficateChange}
                          >
                            <FormControlLabel
                              value={1}
                              control={<Radio />}
                              label="1"
                            />
                            <FormControlLabel
                              value={2}
                              control={<Radio />}
                              label="2"
                            />
                          </RadioGroup>
                        </div>
                      </div>

                      <div className="col-6">
                        <Input
                          label="Certificate Background"
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleCertificateBGUpload(e)}
                        />
                        <span
                          className="text-secondary px-2"
                          style={{ fontSize: 12 }}
                        >
                          For best result use 1.41:1 ratio image.
                        </span>
                        {bgBase64 ? (
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <img src={bgBase64} height={50} />
                            <IconButton
                              onClick={(event) => {
                                if (editingCourseData && bgFile) {
                                  console.log("From bg");
                                  setBgBase64(certificateSetting.bgImage);
                                } else {
                                  console.log("From empty");
                                  handleCertficateChange({
                                    target: { name: "bgImage", value: "" },
                                  });
                                  setBgBase64("");
                                }
                                setBgFile(null);
                              }}
                            >
                              {certificateSetting.bgImage && bgFile ? (
                                <UndoIcon fontSize="small" />
                              ) : (
                                <CloseIcon fontSize="small" />
                              )}
                            </IconButton>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <Input
                      label="Line 1"
                      placeholder="This certificate is present to"
                      name="line1"
                      value={certificateSetting.line1}
                      onChange={handleCertficateChange}
                    />
                    <Textarea
                      className={"mt-3"}
                      label="Line 2"
                      name="line2"
                      value={certificateSetting.line2}
                      onChange={handleCertficateChange}
                      rows={4}
                      maxLength={100}
                    />
                    <Input
                      label="Received In"
                      placeholder="Course Name"
                      name="receivedIn"
                      value={certificateSetting.receivedIn}
                      onChange={handleCertficateChange}
                    />
                  </div>
                </div>
              ) : null}

              {enableCertification ? (
                <>
                  <div className="col-12 col-md-6 mt-3">
                    <div className="col-12 custom-input">
                      <label>Signatories</label>
                      {errors.authorizers && (
                        <div className="errorMessage">{errors.authorizers}</div>
                      )}
                      <div className="col-12 d-flex flex-column align-items-start gap-5">
                        {certificateSetting.numberOfIssuer > 0
                          ? Array.from(
                              { length: certificateSetting.numberOfIssuer },
                              (_, i) => i + 1,
                            )
                              .slice(0, 2)
                              .map((_, index) => (
                                <Authorizers
                                  authorizer={authorizers[index]}
                                  onSignChange={(data) =>
                                    handleAuthorizerSignChanges(data, index)
                                  }
                                  onChange={(data) => {
                                    handleAuthorizerChanges(data, index);
                                  }}
                                />
                              ))
                          : null}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 mt-3">
                    <div className="col-12 custom-input">
                      <label>Logos</label>
                      <div className="col-12 d-flex flex-wrap align-items-start gap-4">
                        {certificateSetting.numberOfLogos > 0
                          ? Array.from(
                              { length: certificateSetting.numberOfLogos },
                              (_, i) => i + 1,
                            )
                              .slice(0, 2)
                              .map((_, index) => (
                                <UploadLogos
                                  logo={logos[index]}
                                  onFileChange={(data) =>
                                    handleLogoChanges(data, index)
                                  }
                                />
                              ))
                          : null}
                      </div>
                      {errors.logos && (
                        <div className="errorMessage">{errors.logos}</div>
                      )}
                    </div>
                  </div>
                </>
              ) : null}

              {enableCertification ? (
                <div className="col-12 mt-4 pt-5 text-center">
                  <h3 className="">Certificate Preview</h3>
                  <p className="text-secondary">
                    Note: This is only preview. Received certificate may vary
                    little different.
                  </p>
                  <div style={{ width: "1000px" }} className="mx-auto w-full">
                    <CertificatePreview
                      title={certificateSetting?.title}
                      line1={certificateSetting?.line1}
                      line2={certificateSetting?.line2}
                      logos={logos
                        .map((logo) => logo.file || logo.url)
                        .slice(0, certificateSetting?.numberOfLogos)}
                      issuers={authorizers
                        .map((issuer) => ({
                          name: issuer.name,
                          designation: issuer.designation,
                          signatureImage: issuer.file || issuer.signatureImage,
                        }))
                        .slice(0, certificateSetting?.numberOfIssuer)}
                      bg={bgBase64}
                      courseName={
                        certificateSetting?.receivedIn ||
                        title ||
                        "[Course Name]"
                      }
                    />
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <div className="mt-3 d-flex justify-content-between">
            <Button type="button" className={"close"} onClick={closePopup}>
              Cancel
            </Button>

            <div className="d-flex justify-content-end gap-2">
              <Button
                type="button"
                className={"outlined"}
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Back
              </Button>

              <Button
                type="button"
                onClick={activeStep === 1 ? handleSubmit : handleNext}
              >
                {activeStep === 1 ? "Submit" : "Next"}
              </Button>
            </div>
          </div>
        </form>
      </Box>
    </Modal>
  );
};

const Authorizers = ({ authorizer = {}, onChange, onSignChange }) => {
  const inputRef = useRef();

  const [isEditing, setIsEditing] = useState(false);
  const [image, setImage] = useState(authorizer.signatureImage);
  const [name, setName] = useState(authorizer.name);
  const [designation, setDesignation] = useState(authorizer.designation);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      onSignChange({ file: file, imageUrl: authorizer.signatureImage });

      const reader = new FileReader();
      reader.onloadend = () => {
        if (authorizer.signatureImage) {
          setIsEditing(true);
        } else {
          setIsEditing(false);
        }
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const onValueChange = (target, setValue) => {
    const { value, name } = target;
    setValue(value);

    onChange({
      ...authorizer,
      [name]: value,
    });
  };

  const handleRemove = () => {
    if (isEditing) {
      onSignChange({ file: null, imageUrl: authorizer.signatureImage });
      setImage(authorizer.signatureImage);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (authorizer?.file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (authorizer.file && authorizer.signatureImage) {
          setIsEditing(true);
        } else {
          setIsEditing(false);
        }
        setImage(reader.result);
      };
      reader.readAsDataURL(authorizer.file);
    } else {
      setImage(authorizer?.signatureImage);
    }
  }, [authorizer]);

  return (
    <Box className="row row-gap-2">
      <div className="d-flex align-items-center gap-2">
        <Box
          className={"position-relative overflow-hidden"}
          sx={{
            width: 100,
            height: 50,
            cursor: "pointer",
          }}
          onClick={() => inputRef.current.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".png"
            className="d-none"
            onChange={handleFileUpload}
          />

          {image ? (
            <img
              src={image}
              alt="Logo"
              style={{ width: "100%", height: "100%" }}
              className="border rounded-2"
            />
          ) : (
            <div
              className="w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                border: "1px dashed #ccc",
                color: "#ccc",
                borderRadius: "4px",
              }}
            >
              <i>Sign</i>
            </div>
          )}
        </Box>

        {isEditing ? (
          <IconButton onClick={handleRemove}>
            <UndoIcon fontSize="small" />
          </IconButton>
        ) : null}
      </div>

      <div>
        <input
          placeholder="Name"
          name="name"
          style={{ paddingInline: 16, width: "100%" }}
          maxLength={25}
          value={name}
          onChange={(e) => onValueChange(e.target, setName)}
        />
      </div>
      <div>
        <input
          placeholder="Designation (Optional)"
          name="designation"
          style={{ paddingInline: 16, width: "100%" }}
          maxLength={25}
          value={designation}
          onChange={(e) => onValueChange(e.target, setDesignation)}
        />
      </div>
    </Box>
  );
};

const UploadLogos = ({ logo, onFileChange }) => {
  const inputRef = useRef();

  const [image, setImage] = useState(logo);

  const [isEditing, setIsEditing] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileChange({ logo: file, logoUrl: logo?.url });
      const reader = new FileReader();
      reader.onloadend = () => {
        if (logo) {
          setIsEditing(true);
        } else {
          setIsEditing(false);
        }
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const handleRemove = () => {
    if (isEditing) {
      onFileChange({ logo: null, logoUrl: logo?.url });
      setImage(logo);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (logo?.file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (logo.file && logo.url) {
          setIsEditing(true);
        } else {
          setIsEditing(false);
        }
        setImage(reader.result);
      };
      reader.readAsDataURL(logo.file);
    } else {
      setImage(logo?.url);
    }
  }, [logo]);

  return (
    <Box
      className={"position-relative overflow-hidden"}
      sx={{
        width: 100,
        height: 100,
        cursor: "pointer",
      }}
      onClick={() => inputRef.current.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".png"
        className="d-none"
        onChange={handleFileUpload}
      />

      {image ? (
        <>
          <img
            src={image}
            alt="Logo"
            style={{ width: "100%", height: "100%" }}
            className="border rounded-2"
          />
          {isEditing ? (
            <IconButton
              className="position-absolute top-0 end-0"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.5)",
              }}
              onClick={(event) => {
                event.stopPropagation();
                handleRemove();
              }}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          ) : null}
        </>
      ) : (
        <div
          className="w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            border: "1px dashed #ccc",
            color: "#ccc",
            borderRadius: "4px",
          }}
        >
          <i>Logo</i>
        </div>
      )}
    </Box>
  );
};

const CourseDropdown = ({ onSelect, selected, currentCourseId }) => {
  const { tokenData } = useSelector((state) => state.loggedUser);

  const [searchText, setSearchText] = useState("");
  const [debounceSearchText, setDebounceSearchText] = useState("");
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Debouncing for search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    (async () => {
      // Initialize Redux store with course data
      let coursess = [];
      if (tokenData.isOrg) {
        coursess = await searchCourses(debounceSearchText, currentCourseId);
      } else {
        coursess = await searchAdminCourses(
          debounceSearchText,
          currentCourseId,
        );
      }

      setCourses(
        coursess.map((course) => ({
          _id: course._id,
          uniqueId: course.uniqueId,
          title: `${course.uniqueId} - ${course.title}`,
        })),
      );
    })();
  }, [debounceSearchText, currentCourseId, tokenData]);

  useEffect(() => {
    setSelectedCourse(selected);
  }, [selected]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      noOptionsText={`No courses found`}
      getOptionLabel={(option) => option?.title}
      filterOptions={(options) => options}
      options={courses}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <li key={option?.uniqueId} {...optionProps}>
            <div className="w-100 d-flex align-items-center">
              {option?.title}
            </div>
          </li>
        );
      }}
      value={selectedCourse}
      onChange={(event, newValue) => {
        setSelectedCourse(newValue);
        if (onSelect) {
          onSelect(newValue);
        }
      }}
      inputValue={searchText}
      onInputChange={(event, newInput) => {
        setSearchText(newInput);
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder="Select Course" />
      )}
    />
  );
};

export default CreateCourse;
